// Words for a presented schedule, in the person's language: the common shapes say what they are,
// anything else says it runs on its own schedule (the panel shows its cron beside it). Weekday names
// and dates come from Intl, never a table of our own.
import { formatCopy } from '@/i18n/ns';
import type { AppTrackerCopyKey } from '@/i18n/ns/app-tracker';
import type { AppSchedulePresentation } from '../schedule-presentation';

type Copy = Record<AppTrackerCopyKey, string>;
const WEEKDAYS = [1, 2, 3, 4, 5];

/** 0 = Sunday … 6 = Saturday, in the person's language (2023-01-01 was a Sunday). */
export function weekdayName(day: number, locale: string, style: 'long' | 'short' = 'long') {
  try {
    return new Intl.DateTimeFormat(locale, { weekday: style, timeZone: 'UTC' }).format(
      new Date(Date.UTC(2023, 0, 1 + day)),
    );
  } catch {
    return String(day);
  }
}

function list(items: string[], locale: string) {
  try {
    return new Intl.ListFormat(locale, { type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

export function scheduleWords(schedule: AppSchedulePresentation, copy: Copy, locale: string) {
  if (schedule.cadence === 'daily') return formatCopy(copy.scheduleDaily, { time: schedule.time });
  if (schedule.cadence === 'interval')
    return schedule.intervalMinutes === 60
      ? copy.scheduleEveryHour
      : formatCopy(copy.scheduleEveryMinutes, { minutes: String(schedule.intervalMinutes) });
  if (schedule.cadence === 'weekly') {
    if (schedule.days.length === 5 && WEEKDAYS.every((day) => schedule.days.includes(day)))
      return formatCopy(copy.scheduleWeekdays, { time: schedule.time });
    return formatCopy(copy.scheduleWeekly, {
      days: list(
        schedule.days.map((day) => weekdayName(day, locale)),
        locale,
      ),
      time: schedule.time,
    });
  }
  return copy.scheduleCustom;
}

/** The calendar year of a moment in a zone (null when the zone or moment cannot be read). */
function yearOf(date: Date, zone?: string) {
  try {
    return new Intl.DateTimeFormat('en', {
      year: 'numeric',
      ...(zone ? { timeZone: zone } : {}),
    }).format(date);
  } catch {
    return null;
  }
}

/** A moment in the person's language and the schedule's own zone, with its year only when that is not this
 * year; null when it cannot be shown. `parts` narrows it to the date or the time of day. */
export function formatMoment(
  value: string | null | undefined,
  locale: string,
  zone?: string,
  parts: 'both' | 'date' | 'time' = 'both',
  now: Date = new Date(),
) {
  if (!value) return null;
  try {
    const date = new Date(value);
    const thisYear = yearOf(date, zone) === yearOf(now, zone);
    return new Intl.DateTimeFormat(locale, {
      ...(parts === 'time' ? {} : { month: 'short', day: 'numeric' }),
      ...(parts === 'time' || thisYear ? {} : { year: 'numeric' }),
      ...(parts === 'date' ? {} : { hour: 'numeric', minute: '2-digit' }),
      ...(zone ? { timeZone: zone } : {}),
    }).format(date);
  } catch {
    return null;
  }
}

/** The runs after the next one, from the server's own list. */
export function laterRuns(schedule: AppSchedulePresentation | null, next: string | null) {
  const runs = schedule?.nextRuns ?? [];
  const after = next ? runs.filter((at) => Date.parse(at) > Date.parse(next)) : runs.slice(1);
  return after.slice(0, 2);
}

/** The later runs as briefly as they read clearly: only their dates when they run at the same time of day as the
 * next run, only their times when they run on its day, otherwise both. */
export function laterRunWords(
  later: readonly string[],
  next: string | null,
  locale: string,
  zone?: string,
) {
  const at = (value: string, parts: 'date' | 'time') => formatMoment(value, locale, zone, parts);
  const parts =
    next && later.every((run) => at(run, 'time') === at(next, 'time'))
      ? 'date'
      : next && later.every((run) => at(run, 'date') === at(next, 'date'))
        ? 'time'
        : 'both';
  return later
    .map((run) => formatMoment(run, locale, zone, parts))
    .filter((value): value is string => Boolean(value));
}
